// File:  src/utils/ErrorHandler.js

/**
 * @fileoverview Global Vue Application Error Handler
 *
 * Primary Purpose: Sets up and configures Vue 3's global error handler for
 * application-wide error catching and logging. This utility is used once during
 * application initialization to establish centralized error handling.
 *
 * Scope: Application-wide error catching and logging infrastructure
 * Usage Pattern: Imported once in main.js via `initializeErrorHandler(app)`
 *
 * Architecture:
 * - Pure utility for global Vue app configuration
 * - Uses @/composables/utils for error message processing
 * - Uses @/utils/announce for user notifications and accessibility
 * - Provides centralized logging functionality
 *
 * Error Flow:
 * - Component try/catch blocks should use announce() with processError()
 * - This handler is a SAFETY NET for uncaught errors
 * - Notifies users via toast + screen reader for better UX
 * - Also logs to console and optional external service
 *
 * Dependencies:
 * - @/composables/utils (for error message extraction)
 * - @/utils/announce (for accessible error announcements)
 *
 * Related Files:
 * - src/composables/utils.ts (error processing composable)
 * - src/utils/announce.ts (accessibility announcements)
 * - src/main.js (where this handler is initialized)
 */

import { processError } from '@/composables/utils'
import { announceError } from '@/utils/announce'
import { logger } from '@/utils/logger'
import * as Sentry from '@sentry/vue'

/**
 * Error deduplication cache
 * Prevents flooding of toast notifications for repeated errors
 * Key: error message, Value: timestamp of last announcement
 */
const errorCache = new Map()
const DEDUPE_WINDOW = 5000 // 5 seconds

/**
 * Check if an error should be announced (not a duplicate within window)
 */
function shouldAnnounceError(message) {
    const now = Date.now()
    const lastAnnounced = errorCache.get(message)

    if (lastAnnounced && now - lastAnnounced < DEDUPE_WINDOW) {
        return false // Skip duplicate within 5s window
    }

    errorCache.set(message, now)
    return true
}

export function initializeErrorHandler(app) {
    app.config.errorHandler = (err, instance, info) => {
        // Log the error for debugging
        logger.error('Uncaught Vue error', {
            error: err.message || String(err),
            component: instance?.$options?.name || 'Unknown',
            errorInfo: info
        })

        // Extract meaningful error message
        const errorMessage = processError(err, 'An unexpected error occurred')

        // DEDUPLICATION: Only announce if not recently announced
        if (shouldAnnounceError(errorMessage)) {
            // Announce to users (toast + screen reader) for accessibility
            // This ensures even uncaught errors are communicated to all users
            announceError(errorMessage, 5000) // Longer duration for unexpected errors
        }

        // Always send to external error tracking (even duplicates)
        sendToErrorTracking(err, { instance, info, userMessage: errorMessage })
    }
}

/**
 * Send error to external tracking service (GlitchTip/Sentry)
 *
 * @param {Error} error - The original error object
 * @param {Object} context - Additional context (component instance, error info, etc.)
 */
function sendToErrorTracking(error, context = {}) {
    try {
        // Send the ORIGINAL error to preserve stack trace
        Sentry.captureException(error, {
            contexts: {
                vue: {
                    componentName: context.instance?.$options?.name || 'Unknown',
                    errorInfo: context.info
                }
            },
            tags: {
                error_handler: 'vue_global'
            },
            extra: {
                userMessage: context.userMessage // The friendly message shown to users
            }
        })

        logger.info('Error sent to tracking service', {
            component: context.instance?.$options?.name || 'Unknown'
        })
    } catch (err) {
        // Don't let error tracking break the app
        logger.error('Failed to send error to tracking service', { error: err.message })
    }
}
